import { useRef, type MouseEvent, type ReactElement } from 'react';
import type { DocumentSection, Proposal, ValidationIssue } from '@shared/types';
import { PageFrame } from '../pages/Page';
import { PageArchetype } from '../pages/registry';
import { READ_ONLY_ACTIONS } from '../pages/types';
import { channel, tacticGroupDisplayTitle } from '../lib/plan';
import { useContentLibrary } from '../state/ContentLibraryContext';
import { ReviewIcon } from './icons';

interface Status {
  label: string;
  tone: 'muted' | 'warn' | 'danger';
}

/** Quiet secondary status for a slide that needs the seller's attention. */
export function statusFor(proposal: Proposal, section: DocumentSection): Status | null {
  if (section.hidden) return { label: 'Hidden', tone: 'muted' };

  if (section.productId) {
    const issue = proposal.validation.errors.find(
      (candidate: ValidationIssue) => candidate.productId === section.productId,
    );
    if (issue) return { label: issue.code === 'CHANNEL_MINIMUM' ? 'Below minimum' : 'Issue', tone: 'danger' };
    if ((section.type === 'product_plan' || section.type === 'media_product') && !channel(proposal, section.productId)) {
      return { label: 'Not in plan', tone: 'warn' };
    }
  }

  if (section.type === 'audience_profiles' && proposal.audience.estimatedPeople === null) {
    return { label: 'Audience unverified', tone: 'warn' };
  }

  if (proposal.validation.errors.length > 0 && (section.type === 'budget_summary' || section.type === 'media_mix')) {
    return { label: `${proposal.validation.errors.length} plan issue${proposal.validation.errors.length === 1 ? '' : 's'}`, tone: 'danger' };
  }

  return null;
}

/** One ordered deck item: full slide preview, then its title and temporary state. */
export function PageCard({
  proposal,
  section,
  updating = false,
  recentlyChanged = false,
  newlyAdded = false,
  building = false,
  onFocus,
  reviewCount = 0,
  onReview,
  placeholder,
}: {
  proposal: Proposal;
  section: DocumentSection;
  updating?: boolean;
  recentlyChanged?: boolean;
  newlyAdded?: boolean;
  building?: boolean;
  onFocus: (sectionId: string) => void;
  reviewCount?: number;
  onReview?: (sectionId: string) => void;
  placeholder?: string;
}): ReactElement {
  const cardRef = useRef<HTMLElement>(null);
  const { productName } = useContentLibrary();
  const status = statusFor(proposal, section);
  const hidden = section.hidden === true;
  const displayTitle = tacticGroupDisplayTitle(
    proposal,
    section.productIds ?? [],
    section.title,
    productName,
  );
  const stateLabel = building
    ? 'Building'
    : updating
      ? 'Updating'
      : recentlyChanged
        ? newlyAdded
          ? 'New page'
          : 'Updated'
        : status?.label;
  const classes = [
    'proposal-slide',
    hidden ? 'proposal-slide-hidden' : '',
    updating ? 'proposal-slide-updating' : '',
    recentlyChanged ? 'proposal-slide-changed' : '',
    newlyAdded ? 'proposal-slide-added' : '',
    building ? 'proposal-slide-building' : '',
  ].filter(Boolean).join(' ');

  const resetTilt = (): void => {
    cardRef.current?.style.removeProperty('--tilt-rx');
    cardRef.current?.style.removeProperty('--tilt-ry');
  };

  const onTiltMove = (event: MouseEvent<HTMLElement>): void => {
    if (building) return;
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const nx = (event.clientX - rect.left) / rect.width - 0.5;
    const ny = (event.clientY - rect.top) / rect.height - 0.5;
    card.style.setProperty('--tilt-ry', `${(nx * 5).toFixed(2)}deg`);
    card.style.setProperty('--tilt-rx', `${(ny * -5).toFixed(2)}deg`);
  };

  const openSlide = (): void => {
    cardRef.current?.classList.add('proposal-slide-gallery-transitioning');
    resetTilt();
    onFocus(section.id);
  };

  return (
    <article
      ref={cardRef}
      id={`proposal-slide-${section.id}`}
      className={classes}
      role="listitem"
      aria-label={`Open ${displayTitle}`}
      tabIndex={0}
      onClick={openSlide}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openSlide();
        }
      }}
      onMouseMove={onTiltMove}
      onMouseLeave={resetTilt}
    >
      <div className="proposal-slide__preview">
        {building ? (
          <div className="build-slide-skeleton build-slide-skeleton-active" aria-label={`Building ${displayTitle}`}>
            <span className="build-skeleton-title" />
            <span className="build-skeleton-line build-skeleton-line-short" />
            <span className="build-skeleton-line" />
            <span className="build-skeleton-block" />
          </div>
        ) : (
          <PageFrame ariaLabel={displayTitle}>
            <PageArchetype proposal={proposal} section={section} actions={READ_ONLY_ACTIONS} readOnly />
          </PageFrame>
        )}
        {updating ? <span className="proposal-slide__pulse" aria-hidden="true" /> : null}
        {placeholder ? <span className="proposal-slide__placeholder">{placeholder}</span> : null}
        {reviewCount > 0 && !building ? (
          <button
            type="button"
            className="proposal-slide__review"
            aria-label={`Review ${reviewCount} ${reviewCount === 1 ? 'item' : 'items'} on ${displayTitle}`}
            title={`${reviewCount} ${reviewCount === 1 ? 'item needs' : 'items need'} review`}
            onClick={(event) => {
              event.stopPropagation();
              onReview?.(section.id);
            }}
          >
            <ReviewIcon />
            <span>{reviewCount}</span>
          </button>
        ) : null}
      </div>
      <div className="proposal-slide__caption">
        <span className="proposal-slide__title">{displayTitle}</span>
        {stateLabel ? (
          <span className={`proposal-slide__status${status && !updating && !recentlyChanged && !building ? ` proposal-slide__status-${status.tone}` : ''}`}>
            {stateLabel}
          </span>
        ) : null}
      </div>
    </article>
  );
}
